import { get, set } from "idb-keyval";

import JSZip from "jszip";
import { PyodideInterface } from "pyodide";
import { projectService } from "../services/ProjectService";
import { pyodideService } from "../services/PyodideService";

// Constants
const FS_SNAPSHOT_PREFIX = "fs-snapshot:";
const FS_FILE_PREFIX = "fs-file:"; // New prefix for individual file storage

export class FilesystemService {
  pyodide: PyodideInterface | null = null;
  pendingFiles: Record<string, any> = {};
  private autoSaveTimer: NodeJS.Timeout | null = null;
  private isWorkerMode: boolean = false;

  setPyodide(pyodide: PyodideInterface) {
    this.pyodide = pyodide;
    // Start auto-save when Pyodide is set
    this.startAutoSave();
  }

  /**
   * Set whether we're using worker mode
   */
  setWorkerMode(workerMode: boolean) {
    this.isWorkerMode = workerMode;
    if (workerMode) {
      // Start auto-save in worker mode
      this.startAutoSave();
    }
  }

  async handlePyodideReady(): Promise<boolean> {
    // Once Pyodide is ready, we can commit any pending files
    try {
      console.log("Handling Pyodide ready in filesystem service");
      if (Object.keys(this.pendingFiles).length > 0) {
        console.log(`Committing ${Object.keys(this.pendingFiles).length} pending files to Pyodide filesystem`);
        await this._commitPendingFiles();
        // Dispatch an event to signal filesystem is ready
        window.dispatchEvent(new CustomEvent('filesystem:ready'));
        return true;
      }
      // Even if no pending files, dispatch the ready event
      window.dispatchEvent(new CustomEvent('filesystem:ready'));
      return true;
    } catch (error) {
      console.error("Error handling Pyodide ready:", error);
      return false;
    }
  }

  // List files in a directory in Pyodide filesystem
  async listPyodideFiles(directory: string = "/project") {
    if (this.isWorkerMode) {
      let combinedSnapshot: Record<string, any> = {};

      try {
        // First try to load from IndexedDB directly
        const projectId = projectService.getCurrentProjectId();
        if (projectId) {
          const key = `${FS_SNAPSHOT_PREFIX}${projectId}`;
          const fsSnapshot = await get(key);
          if (fsSnapshot) {
            combinedSnapshot = { ...fsSnapshot };
          }
        }
      } catch (error) {
        console.warn("Error loading snapshot from IndexedDB:", error);
      }

      // If we have an active Pyodide worker, get the latest snapshot
      if (pyodideService.isReady()) {
        try {
          const workerSnapshot = await pyodideService.getFilesystemSnapshot();
          if (workerSnapshot) {
            combinedSnapshot = { ...workerSnapshot };
          }
        } catch (error) {
          console.warn("Error getting snapshot from Pyodide:", error);
        }
      }

      // Apply any pending files
      for (const [path, content] of Object.entries(this.pendingFiles)) {
        if (path.startsWith("/project/")) {
          const relativePath = path.substring("/project/".length);
          if (content === null) {
            // File marked for deletion - remove from snapshot
            delete combinedSnapshot[relativePath];
          } else {
            // New or modified file - add to snapshot
            combinedSnapshot[relativePath] = {
              type: typeof content === "string" ? "text" : "binary",
              content: content,
            };
          }
        }
      }

      // Convert snapshot to directory structure
      const rootDir = { path: "project", type: "folder", contents: [] };
      const dirMap: Record<string, any> = { "/project": rootDir };

      for (const [relativePath, fileData] of Object.entries(combinedSnapshot)) {
        const fullPath = `/project/${relativePath}`;
        const pathParts = relativePath.split("/");
        const fileName = pathParts.pop() || "";

        // Build directory path
        let currentPath = "/project";
        for (const part of pathParts) {
          const partPath = `${currentPath}/${part}`;
          currentPath = partPath;

          // Create directory entry if it doesn't exist
          if (!dirMap[partPath]) {
            const parentPath = currentPath.substring(
              0,
              currentPath.lastIndexOf("/")
            );
            const parent = dirMap[parentPath];

            if (parent) {
              const newDir = {
                path: currentPath,
                name: part,
                type: "folder",
                contents: [],
              };
              parent.contents.push(newDir);
              dirMap[partPath] = newDir;
            }
          }
        }

        // Add file entry
        const parentDir = dirMap[currentPath];
        if (parentDir) {
          parentDir.contents.push({
            path: fullPath,
            name: fileName,
            type: "file",
            size: (fileData as any).content.length,
          });
        }
      }

      // return rootDir.contents;
      // Filter to just the requested directory
      if (directory === "/project") {
        return rootDir.contents;
      } else {
        return dirMap[directory]?.contents || [];
      }
    } else if (this.pyodide) {
      const result = await this.pyodide.runPythonAsync(`
      import os
      import json

      def list_files_recursive(directory):
          # Build a dictionary representation first
          root_dict = {"path": os.path.basename(directory), "type": "folder", "contents": []}
          dir_dict = {directory: root_dict}

          for root, dirs, files in os.walk(directory):
              # Get or create the current directory entry
              current_dir = dir_dict[root]

              # Add files to current directory
              for file in files:
                  full_path = os.path.join(root, file)
                  current_dir["contents"].append({
                      "path": file,
                      "type": "file",
                      "size": os.path.getsize(full_path)
                  })

              # Create entries for subdirectories
              for dir_name in dirs:
                  full_path = os.path.join(root, dir_name)
                  new_dir = {
                      "path": dir_name,
                      "type": "folder",
                      "contents": []
                  }
                  current_dir["contents"].append(new_dir)
                  dir_dict[full_path] = new_dir

          # Return just the contents of the root directory
          return root_dict["contents"]

      json.dumps(list_files_recursive("${directory}"))
    `);

      return JSON.parse(result);
    }

    // Try to load from IndexedDB
    try {
      const projectId = projectService.getCurrentProjectId();
      if (projectId) {
        const key = `${FS_SNAPSHOT_PREFIX}${projectId}`;
        const fsSnapshot = await get(key);
        if (fsSnapshot) {
          // Convert snapshot to directory structure
          const rootDir = { path: "project", type: "folder", contents: [] };
          const dirMap: Record<string, any> = { "/project": rootDir };

          for (const [relativePath, fileData] of Object.entries(fsSnapshot)) {
            const fullPath = `/project/${relativePath}`;
            const pathParts = relativePath.split("/");
            const fileName = pathParts.pop() || "";

            // Build directory path
            let currentPath = "/project";
            for (const part of pathParts) {
              const partPath = `${currentPath}/${part}`;
              currentPath = partPath;

              // Create directory entry if it doesn't exist
              if (!dirMap[partPath]) {
                const parentPath = currentPath.substring(
                  0,
                  currentPath.lastIndexOf("/")
                );
                const parent = dirMap[parentPath];

                if (parent) {
                  const newDir = {
                    path: currentPath,
                    name: part,
                    type: "folder",
                    contents: [],
                  };
                  parent.contents.push(newDir);
                  dirMap[partPath] = newDir;
                }
              }
            }

            // Add file entry
            const parentDir = dirMap[currentPath];
            if (parentDir) {
              parentDir.contents.push({
                path: fullPath,
                name: fileName,
                type: "file",
                size: (fileData as any).content.length,
              });
            }
          }

          if (directory === "/project") {
            return rootDir.contents;
          } else {
            return dirMap[directory]?.contents || [];
          }
        }
      }
    } catch (error) {
      console.warn("Error listing files from IndexedDB:", error);
    }

    // Return empty array as fallback
    console.warn("No file system available, returning empty files list");
    return [];
  }

  // TODO: this is getting out of date
  // Save a file from Pyodide to native FS
  async saveToNativeFS(
    pyodidePath: string,
    directoryHandle: FileSystemDirectoryHandle
  ) {
    if (!this.pyodide) throw new Error("Pyodide not initialized");

    // Read file content from Pyodide
    const content = await this.pyodide.runPythonAsync(`
      with open("${pyodidePath}", "r") as f:
          f.read()
    `);

    // Get the file name from the path
    const fileName = pyodidePath.split("/").pop() || "untitled";

    // Write to native filesystem
    const fileHandle = await directoryHandle.getFileHandle(fileName, {
      create: true,
    });
    const writable = await fileHandle.createWritable();
    await writable.write(content);
    await writable.close();

    return fileName;
  }

  // Improved exportProject function
  async exportProject(directoryHandle: FileSystemDirectoryHandle) {
    let snapshot;

    // Get filesystem snapshot
    if (this.isWorkerMode) {
      snapshot = await pyodideService.getFilesystemSnapshot();
    } else if (this.pyodide) {
      snapshot = await this.serializeFilesystem();
    } else {
      // Try to get snapshot from IndexedDB
      const projectId = projectService.getCurrentProjectId();
      if (projectId) {
        const key = `${FS_SNAPSHOT_PREFIX}${projectId}`;
        snapshot = await get(key);
        if (!snapshot) {
          throw new Error("No filesystem snapshot available in IndexedDB");
        }
      } else {
        throw new Error("No project ID available for export");
      }
    }

    if (!snapshot) {
      throw new Error("Failed to get filesystem snapshot");
    }

    // Process each file and directory
    for (const [relativePath, fileData] of Object.entries(snapshot)) {
      // Get full path components
      const pathParts = relativePath.split("/");
      const fileName = pathParts.pop()!;

      // Create nested directories as needed
      let currentDir = directoryHandle;
      for (const part of pathParts) {
        currentDir = await currentDir.getDirectoryHandle(part, {
          create: true,
        });
      }

      // Write the file
      try {
        const fileHandle = await currentDir.getFileHandle(fileName, {
          create: true,
        });
        const writable = await fileHandle.createWritable();

        const data = fileData as { type: string; content: string };

        if (data.type === "text") {
          await writable.write(data.content);
        } else if (data.type === "binary") {
          const binaryContent = this.base64ToUint8Array(data.content);
          const arrayBuffer = new ArrayBuffer(binaryContent.length);
          const view = new Uint8Array(arrayBuffer);
          view.set(binaryContent);
          await writable.write(arrayBuffer);
        }

        await writable.close();
      } catch (error) {
        console.error(`Error exporting file ${relativePath}:`, error);
      }
    }

    return "Project exported successfully";
  }

  async downloadProjectAsZip(): Promise<void> {
    try {
      let snapshot;

      if (this.isWorkerMode && pyodideService.isReady()) {
        try {
          snapshot = await pyodideService.getFilesystemSnapshot();
        } catch (error) {
          console.warn('Failed to get snapshot from worker mode:', error);
        }
      }

      if (!snapshot && this.pyodide) {
        try {
          snapshot = await this.serializeFilesystem();
        } catch (error) {
          console.warn('Failed to serialize filesystem:', error);
        }
      }

      if (!snapshot) {
        const projectId = projectService.getCurrentProjectId();
        if (projectId) {
          const key = `${FS_SNAPSHOT_PREFIX}${projectId}`;
          try {
            snapshot = await get(key);
          } catch (error) {
            console.warn('Failed to get snapshot from IndexedDB:', error);
          }
        }
      }

      if (!snapshot) {
        snapshot = {} as { [key: string]: { type: string; content: string } };

        if (this.pyodide || pyodideService.isReady()) {
          try {
            const fileList = await this.listPyodideFiles("/project");
            for (const filePath of fileList) {
              try {
                const content = await this.readFileFromPyodide(filePath);
                if (content !== null) {
                  const isBinary = this._isBinaryFile(filePath);
                  if (isBinary && typeof content === 'string') {
                    (snapshot as any)[filePath] = { type: "binary", content: content };
                  } else if (typeof content === 'string') {
                    (snapshot as any)[filePath] = { type: "text", content: content };
                  }
                }
              } catch (fileError) {
                console.warn(`Failed to read file ${filePath}:`, fileError);
              }
            }
          } catch (listError) {
            console.warn('Failed to list files from pyodide:', listError);
          }
        }

        if (Object.keys(snapshot).length === 0 && this.pendingFiles) {
          for (const [path, content] of Object.entries(this.pendingFiles)) {
            if (typeof content === 'string') {
              (snapshot as any)[path] = { type: "text", content: content };
            } else if (content instanceof ArrayBuffer || content instanceof Uint8Array) {
              const base64 = this._arrayBufferToBase64(content);
              (snapshot as any)[path] = { type: "binary", content: base64 };
            }
          }
        }
      }

      const zip = new JSZip();

      for (const [relativePath, fileData] of Object.entries(snapshot)) {
        const data = fileData as { type: string; content: string };

        let cleanPath = relativePath;
        if (cleanPath.startsWith('/project/')) {
          cleanPath = cleanPath.substring('/project/'.length);
        }
        if (cleanPath.startsWith('/')) {
          cleanPath = cleanPath.substring(1);
        }

        if (data.type === "text") {
          zip.file(cleanPath, data.content);
        } else if (data.type === "binary") {
          const binaryContent = this.base64ToUint8Array(data.content);
          zip.file(cleanPath, binaryContent);
        }
      }

      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: {
          level: 6
        }
      });

      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;

      const projectId = projectService.getCurrentProjectId();
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
      const fileName = `${projectId || 'webwald-project'}-${timestamp}.zip`;

      link.download = fileName;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      console.log(`Project downloaded as ${fileName} with ${Object.keys(snapshot).length} files`);
    } catch (error) {
      console.error('Error downloading project as zip:', error);
      throw error;
    }
  }

  private _isBinaryFile(filePath: string): boolean {
    const extension = filePath.split('.').pop()?.toLowerCase() || '';
    const binaryExtensions = [
      'png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'ico', 'svg',
      'pdf', 'zip', 'tar', 'gz', 'mp3', 'mp4', 'wav', 'ogg',
      'exe', 'dll', 'so', 'bin', 'dat'
    ];
    return binaryExtensions.includes(extension);
  }

  // TODO: not used currently
  // Import a file from native FS to Pyodide
  async importToPyodide(
    fileHandle: FileSystemFileHandle,
    pyodidePath: string = "/project"
  ) {
    if (!this.pyodide) throw new Error("Pyodide not initialized");

    const file = await fileHandle.getFile();
    const content = await file.text();
    const fileName = fileHandle.name;

    // Write to Pyodide filesystem
    await this.pyodide.runPythonAsync(`
      import os
      os.makedirs("${pyodidePath}", exist_ok=True)
      with open("${pyodidePath}/${fileName}", "w") as f:
          f.write("""${content.replace(/"""/g, '\\"\\"\\"')}""")
    `);

    return `${pyodidePath}/${fileName}`;
  }

  /**
   * Import a directory to Pyodide filesystem
   */
  async importDirectoryToPyodide(
    dirHandle: FileSystemDirectoryHandle,
    pyodidePath: string = "/project"
  ) {
    // Create the directory in Pyodide
    if (this.isWorkerMode) {
      // For worker mode, we'll read all files and send them as a batch
      const files: Record<string, string | ArrayBuffer> = {};

      // Recursive function to process directories
      const processDirectory = async (
        handle: FileSystemDirectoryHandle,
        currentPath: string
      ) => {
        for await (const [name, entry] of handle.entries()) {
          const entryPath = `${currentPath}/${name}`;

          try {
            if (entry.kind === "file") {
              const file = await (entry as FileSystemFileHandle).getFile();
              // Check if it's a binary file
              const isBinary =
                file.type.startsWith("image/") ||
                file.name.endsWith(".png") ||
                file.name.endsWith(".jpg") ||
                file.name.endsWith(".jpeg") ||
                file.name.endsWith(".gif") ||
                file.name.endsWith(".ico");

              if (isBinary) {
                const content = await file.arrayBuffer();
                files[entryPath] = content;
              } else {
                const content = await file.text();
                files[entryPath] = content;
              }
            } else if (entry.kind === "directory") {
              // Process subdirectory
              await processDirectory(
                entry as FileSystemDirectoryHandle,
                entryPath
              );
            }
          } catch (error) {
            console.error(`Error processing ${entry.kind} ${name}:`, error);
          }
        }
      };

      // Process all files
      await processDirectory(dirHandle, pyodidePath);

      // Add to pending files
      this.pendingFiles = { ...this.pendingFiles, ...files };

      // Only commit if Pyodide is ready
      if (pyodideService.isReady()) {
        await this._commitPendingFiles();
      }
    } else if (this.pyodide) {
      // Direct Pyodide mode
      // Create the directory in Pyodide
      try {
        this.pyodide.FS.mkdirTree(pyodidePath);
      } catch (e) {
        // Directory might already exist, that's fine
        console.log(`Directory ${pyodidePath} might already exist`);
      }

      // Recursive function to process directories
      const processDirectory = async (
        handle: FileSystemDirectoryHandle,
        currentPath: string
      ) => {
        for await (const [name, entry] of handle.entries()) {
          const entryPath = `${currentPath}/${name}`;

          try {
            if (entry.kind === "file") {
              const file = await (entry as FileSystemFileHandle).getFile();
              // Check if it's a binary file
              const isBinary =
                file.type.startsWith("image/") ||
                file.name.endsWith(".png") ||
                file.name.endsWith(".jpg") ||
                file.name.endsWith(".jpeg") ||
                file.name.endsWith(".gif") ||
                file.name.endsWith(".ico");

              if (isBinary) {
                const content = await file.arrayBuffer();
                const uint8Array = new Uint8Array(content);
                this.pyodide!.FS.writeFile(entryPath, uint8Array);
              } else {
                const content = await file.text();
                this.pyodide!.FS.writeFile(entryPath, content);
              }
              console.log(`File written: ${entryPath}`);
            } else if (entry.kind === "directory") {
              // Create directory
              try {
                this.pyodide!.FS.mkdirTree(entryPath);
              } catch (e) {
                // Directory might already exist
                console.log(`Directory ${entryPath} might already exist`);
              }

              // Process subdirectory
              await processDirectory(
                entry as FileSystemDirectoryHandle,
                entryPath
              );
            }
          } catch (error) {
            console.error(`Error processing ${entry.kind} ${name}:`, error);
          }
        }
      };

      await processDirectory(dirHandle, pyodidePath);
    } else {
      throw new Error("No Pyodide instance available");
    }

    // Save to IndexedDB after import
    await this.saveToIndexedDB();

    return pyodidePath;
  }

  // New method to read file directly from IndexedDB without requiring Pyodide
  private async readFileFromIndexedDB(path: string): Promise<string | ArrayBuffer | null> {
    // Extract relative path
    const relativePath = path.startsWith("/project/")
      ? path.substring("/project/".length)
      : path;

    const projectId = projectService.getCurrentProjectId();
    if (!projectId) return null;

    try {
      // First try to get individual file from IndexedDB
      const fileKey = `${FS_FILE_PREFIX}${projectId}:${relativePath}`;
      let fileData = await get(fileKey);

      // If file not found as individual entry, try to get from snapshot
      if (fileData === undefined) {
        const snapshotKey = `${FS_SNAPSHOT_PREFIX}${projectId}`;
        const snapshot = await get(snapshotKey);

        if (snapshot && snapshot[relativePath]) {
          fileData = snapshot[relativePath];
        }
      }

      if (!fileData) return null;

      if (fileData.type === "binary") {
        return this.base64ToArrayBuffer(fileData.content);
      } else {
        return fileData.content;
      }
    } catch (error) {
      console.error(`Error reading file from IndexedDB: ${error}`);
      return null;
    }
  }

  // New method to save file directly to IndexedDB
  private async writeFileToIndexedDB(path: string, content: string | ArrayBuffer): Promise<boolean> {
    const relativePath = path.startsWith("/project/")
      ? path.substring("/project/".length)
      : path;

    const projectId = projectService.getCurrentProjectId();
    if (!projectId) return false;

    try {
      // Get file type based on content
      const isBinary = typeof content !== 'string';

      // Format file data
      const fileData = {
        type: isBinary ? "binary" : "text",
        content: isBinary
          ? this._arrayBufferToBase64(content as ArrayBuffer)
          : content
      };

      // Store as individual file for faster access
      const fileKey = `${FS_FILE_PREFIX}${projectId}:${relativePath}`;
      await set(fileKey, fileData);

      // Update the snapshot as well on next autosave
      this.pendingFiles[`/project/${relativePath}`] = content;

      return true;
    } catch (error) {
      console.error(`Error writing file to IndexedDB: ${error}`);
      return false;
    }
  }

  // Get content of a file from Pyodide
  async readFileFromPyodide(path: string) {
    // First check if file is in pending files
    if (this.pendingFiles[path] !== undefined) {
      return this.pendingFiles[path];
    }

    // Then try to read from IndexedDB
    const indexDBContent = await this.readFileFromIndexedDB(path);
    if (indexDBContent !== null) {
      return indexDBContent;
    }

    // Fall back to Pyodide worker if available
    if (this.isWorkerMode) {
      if (!pyodideService.isReady()) {
        throw new Error(`Cannot read file: ${path} - Pyodide is not ready`);
      }

      let snapshot = await pyodideService.getFilesystemSnapshot();
      const maxRetries = 5;
      let retries = 0;
      while (!snapshot && retries < maxRetries) {
        await new Promise((res) => setTimeout(res, 100));
        snapshot = await pyodideService.getFilesystemSnapshot();
        retries++;
      }
      if (!snapshot) {
        throw new Error(`Cannot read file: ${path} - snapshot not available`);
      }

      // Extract relative path
      const relativePath = path.startsWith("/project/")
        ? path.substring("/project/".length)
        : path;

      const fileData = snapshot[relativePath];
      if (!fileData) {
        throw new Error(`File not found: ${path}`);
      }

      if (fileData.type === "binary") {
        // For binary files, convert base64 to Uint8Array
        const base64Content = fileData.content;
        if (typeof base64Content === "string") {
          // If it's already a data URI, return it directly
          if (base64Content.startsWith("data:")) {
            return base64Content;
          }
          return this.base64ToUint8Array(base64Content);
        }
        return fileData.content;
      } else {
        // For text files, return content directly
        return fileData.content;
      }
    } else if (this.pyodide) {
      // Direct Pyodide mode
      const FS = this.pyodide.FS as any;
      return FS.readFile(path, { encoding: "utf8" });
    }

    throw new Error(`File not found: ${path}`);
  }

  // Write content to a file in Pyodide filesystem
  async writeFileToPyodide(path: string, content: string) {
    // Always store in pending files
    this.pendingFiles[path] = content;

    // Always write to IndexedDB regardless of Pyodide state
    await this.writeFileToIndexedDB(path, content);

    if (this.isWorkerMode) {
      if (pyodideService.isReady()) {
        // Batch upload changes to worker
        await this._commitPendingFiles();
      }

      // Dispatch an event to signal the file has changed
      window.dispatchEvent(new CustomEvent('filesystem:file-changed', {
        detail: { path }
      }));

      return path;
    } else if (this.pyodide) {
      // Direct Pyodide mode
      // Ensure directory exists
      const directory = path.substring(0, path.lastIndexOf("/"));
      if (directory) {
        await this.pyodide.runPythonAsync(`
          import os
          os.makedirs("${directory}", exist_ok=True)
        `);
      }

      this.pyodide.FS.writeFile(path, content);

      // Trigger auto-save
      this.triggerAutoSave();

      // Dispatch an event to signal the file has changed
      window.dispatchEvent(new CustomEvent('filesystem:file-changed', {
        detail: { path }
      }));

      return path;
    }

    // Dispatch an event to signal the file has changed even if Pyodide is not available
    window.dispatchEvent(new CustomEvent('filesystem:file-changed', {
      detail: { path }
    }));

    return path; // Return the path even if Pyodide is not available
  }

  // Create a new directory in Pyodide filesystem
  async createDirectory(path: string) {
    if (this.isWorkerMode) {
      // Just create an empty .keep file in the directory
      const keepFilePath = `${path}/.keep`;
      await this.writeFileToPyodide(keepFilePath, "");
      return path;
    } else if (this.pyodide) {
      // Direct Pyodide mode
      await this.pyodide.runPythonAsync(`
        import os
        os.makedirs("${path}", exist_ok=True)
      `);

      // Trigger auto-save
      this.triggerAutoSave();

      return path;
    }

    // Create directory entry in IndexedDB
    const projectId = projectService.getCurrentProjectId();
    if (projectId) {
      // Create a .keep file to represent the directory
      const keepFilePath = `${path}/.keep`;
      await this.writeFileToIndexedDB(keepFilePath, "");
    }

    return path;
  }

  // Delete a file or directory in Pyodide filesystem
  async deleteFileOrDirectory(path: string) {
    // Mark as deleted in pending files
    this.pendingFiles[path] = null;

    // Delete from IndexedDB
    const relativePath = path.startsWith("/project/")
      ? path.substring("/project/".length)
      : path;

    const projectId = projectService.getCurrentProjectId();
    if (projectId) {
      try {
        const fileKey = `${FS_FILE_PREFIX}${projectId}:${relativePath}`;
        await set(fileKey, undefined);
      } catch (error) {
        console.error(`Error deleting file from IndexedDB: ${error}`);
      }
    }

    if (this.isWorkerMode) {
      // Trigger auto-save to commit the deletion
      this.triggerAutoSave();

      return true;
    } else if (this.pyodide) {
      // Direct Pyodide mode
      const isDirectory = await this.pyodide.runPythonAsync(`
        import os
        os.path.isdir("${path}")
      `);

      if (isDirectory) {
        await this.pyodide.runPythonAsync(`
          import shutil
          shutil.rmtree("${path}")
        `);
      } else {
        await this.pyodide.runPythonAsync(`
          import os
          os.remove("${path}")
        `);
      }

      // Trigger auto-save
      this.triggerAutoSave();

      return true;
    }

    return true;
  }

  // Rename a file or directory in Pyodide filesystem
  async renameFileOrDirectory(oldPath: string, newPath: string) {
    // For worker mode, we need to read the content first, then write it to the new path
    try {
      const content = await this.readFileFromPyodide(oldPath);
      await this.writeFileToPyodide(newPath, content);
      await this.deleteFileOrDirectory(oldPath);
      return newPath;
    } catch (error) {
      console.error(`Error renaming ${oldPath} to ${newPath}:`, error);
      throw error;
    }
  }

  // Commit pending file changes to worker
  private async _commitPendingFiles() {
    if (!this.isWorkerMode || Object.keys(this.pendingFiles).length === 0) {
      return true;
    }

    // Send all pending files to worker
    await pyodideService.loadFiles(this.pendingFiles);

    // Clear pending files
    this.pendingFiles = {};

    // Trigger auto-save
    this.triggerAutoSave();

    // Notify that filesystem is ready
    window.dispatchEvent(new CustomEvent('filesystem:ready'));

    return true;
  }

  // Save filesystem to IndexedDB
  async saveToIndexedDB(): Promise<boolean> {
    const projectId = projectService.getCurrentProjectId();
    if (!projectId) return false;

    try {
      let fsSnapshot;

      if (this.isWorkerMode) {
        // Get snapshot from worker
        if (pyodideService.isReady()) {
          // Get snapshot from worker
          fsSnapshot = await pyodideService.getFilesystemSnapshot();
        } else {
          // If Pyodide isn't ready, use pendingFiles as snapshot
          const start_acc: Record<string, any> = {};
          fsSnapshot = Object.keys(this.pendingFiles).reduce((acc, path) => {
            const relativePath = path.startsWith("/project/")
              ? path.substring("/project/".length)
              : path;

            const content = this.pendingFiles[path];
            // Skip deleted files (content === null)
            if (content !== null) {
              // Determine if it's binary based on the content type
              const isBinary =
                content instanceof ArrayBuffer ||
                content instanceof Uint8Array ||
                typeof content !== "string";

              if (isBinary) {
                // Convert binary data to base64 if needed
                let base64Content;
                if (
                  content instanceof ArrayBuffer ||
                  content instanceof Uint8Array
                ) {
                  base64Content = this._arrayBufferToBase64(content);
                } else {
                  // Already in base64 or some other format
                  base64Content = content;
                }

                acc[relativePath] = {
                  type: "binary",
                  content: base64Content,
                  mimeType: this._getMimeType(path),
                };
              } else {
                // Text content
                acc[relativePath] = {
                  type: "text",
                  content: content,
                };
              }
            }

            return acc;
          }, start_acc);
        }
      } else if (this.pyodide) {
        // Get snapshot from direct Pyodide
        fsSnapshot = await this.serializeFilesystem();
      } else {
        // Try to get existing snapshot
        const key = `${FS_SNAPSHOT_PREFIX}${projectId}`;
        const existingSnapshot = await get(key);

        // Merge with pending files
        if (existingSnapshot) {
          fsSnapshot = { ...existingSnapshot };

          // Apply pending changes
          for (const [path, content] of Object.entries(this.pendingFiles)) {
            if (path.startsWith("/project/")) {
              const relativePath = path.substring("/project/".length);

              if (content === null) {
                // Deletion
                delete fsSnapshot[relativePath];
              } else {
                // New or modified file
                const isBinary =
                  content instanceof ArrayBuffer ||
                  content instanceof Uint8Array;

                fsSnapshot[relativePath] = {
                  type: isBinary ? "binary" : "text",
                  content: isBinary
                    ? this._arrayBufferToBase64(content as ArrayBuffer)
                    : content
                };
              }
            }
          }
        } else {
          // No existing snapshot, can't create one without Pyodide
          return false;
        }
      }

      if (!fsSnapshot) return false;

      const key = `${FS_SNAPSHOT_PREFIX}${projectId}`;
      await set(key, fsSnapshot);

      return true;
    } catch (error) {
      console.error("Error saving filesystem to IndexedDB:", error);
      return false;
    }
  }

  // Load filesystem from IndexedDB
  async loadFromIndexedDB(projectId: string): Promise<boolean> {

    try {
      const key = `${FS_SNAPSHOT_PREFIX}${projectId}`;
      const fsSnapshot = await get(key);
      // const fsSnapshot = await projectService.loadFilesystemSnapshot(projectId);

      if (!fsSnapshot) {
        console.log(`No filesystem snapshot found for project: ${projectId}`);
        return false;
      }

      console.log(`Filesystem snapshot found for project: ${projectId}, loading...`);

      // If Pyodide is ready, load files into it
      if (pyodideService.isReady()) {
        console.log("Pyodide ready, loading filesystem into Pyodide");
        const success = await this.deserializeFilesystem(fsSnapshot);

        if (!success) {
          console.error(`Failed to deserialize filesystem for project: ${projectId}`);

          console.log("Retrying filesystem restoration...");
          await new Promise(resolve => setTimeout(resolve, 2000));
          const retrySuccess = await this.deserializeFilesystem(fsSnapshot);

          if (!retrySuccess) {
            console.error(`Retry failed for project: ${projectId}`);
            // Continue anyway - we can use IndexedDB directly
          }
        }
      } else {
        // Just keep the snapshot in memory to use via IndexedDB later
        console.log("Pyodide not ready, filesystem will be loaded when Pyodide is ready");
        // Store all files in pendingFiles for later loading
        for (const [relativePath, fileData] of Object.entries(fsSnapshot)) {
          const path = `/project/${relativePath}`;
          const data = fileData as any;

          if (data.type === "binary") {
            // For binary, store as ArrayBuffer
            const content = this.base64ToArrayBuffer(data.content);
            this.pendingFiles[path] = content;
          } else {
            // For text, store as string
            this.pendingFiles[path] = data.content;
          }
        }
      }

      // Set filesystem ready flag in UI store
      // This needs to be handled by the caller

      console.log(`Successfully prepared filesystem for project: ${projectId}`);
      return true;
    } catch (error) {
      console.error(`Error loading filesystem from IndexedDB for project: ${projectId}`, error);
      return false;
    }
  }

  // Serialize the entire filesystem to a JSON structure
  private async serializeFilesystem(): Promise<any> {
    if (!this.pyodide) throw new Error("Pyodide not initialized");

    const fsSnapshot = await this.pyodide.runPythonAsync(`
      import os
      import json
      import base64
      
      def serialize_fs(root_dir='/project'):
          result = {}
          for root, dirs, files in os.walk(root_dir):
              for file in files:
                  full_path = os.path.join(root, file)
                  rel_path = os.path.relpath(full_path, root_dir)
                  
                  try:
                      with open(full_path, 'r') as f:
                        content = f.read()
                      result[rel_path] = {'type': 'text', 'content': content}
                  except UnicodeDecodeError:
                      # Handle binary files
                      with open(full_path, 'rb') as f:
                        binary_content = f.read()
                      encoded = base64.b64encode(binary_content).decode('ascii')
                      result[rel_path] = {'type': 'binary', 'content': encoded}
                  
          return result
      
      json.dumps(serialize_fs())
    `);

    return JSON.parse(fsSnapshot);
  }

  // Deserialize filesystem from IndexedDB to Pyodide
  private async deserializeFilesystem(fsSnapshot: any): Promise<boolean> {
    console.log("Starting filesystem deserialization");

    if (!fsSnapshot) {
      console.error("No filesystem snapshot provided");
      return false;
    }

    const filesToLoad: Record<string, string | ArrayBuffer> = {};

    // Process snapshot files
    for (const [relativePath, fileData] of Object.entries(fsSnapshot)) {
      const path = `/project/${relativePath}`;
      const type = (fileData as any).type;
      const content = (fileData as any).content;

      if (type === "binary") {
        try {
          // Handle binary files - convert base64 to ArrayBuffer
          const binaryData = this.base64ToArrayBuffer(content);
          filesToLoad[path] = binaryData;
        } catch (error) {
          console.error(`Error processing binary file ${path}:`, error);
        }
      } else {
        // Text files
        filesToLoad[path] = content;
      }
    }

    // Set a maximum number of retries for loading into Pyodide
    const MAX_RETRIES = 3;

    for (let retry = 0; retry <= MAX_RETRIES; retry++) {
      try {
        if (pyodideService.isReady()) {
          const success = await pyodideService.loadFiles(filesToLoad);

          if (success) {
            console.log("Successfully loaded files into Pyodide");
            // Dispatch filesystem ready event
            window.dispatchEvent(new CustomEvent('filesystem:ready'));
            return true;
          } else {
            console.error(`Failed to load files into Pyodide (attempt ${retry + 1}/${MAX_RETRIES + 1})`);
            if (retry < MAX_RETRIES) {
              // Wait before retrying
              await new Promise(resolve => setTimeout(resolve, 1000 * (retry + 1)));
            }
          }
        } else if (retry < MAX_RETRIES) {
          console.log("Pyodide not ready, waiting before retry...");
          await new Promise(resolve => setTimeout(resolve, 1000 * (retry + 1)));
        } else {
          console.error("Pyodide not ready after maximum retries");

          console.log("Storing files in pendingFiles for later loading");
          Object.entries(filesToLoad).forEach(([path, content]) => {
            this.pendingFiles[path] = content;
          });

          return false;
        }
      } catch (error) {
        console.error(`Error loading files into Pyodide (attempt ${retry + 1}/${MAX_RETRIES + 1}):`, error);
        if (retry < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (retry + 1)));
        }
      }
    }

    console.error("Failed to load filesystem after all retries");
    return false;
  }

  // Convert base64 string to Uint8Array for binary files
  public base64ToUint8Array(base64: string): Uint8Array {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  public base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  // Convert ArrayBuffer to base64
  private _arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
    const binary = [];
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary.push(String.fromCharCode(bytes[i]));
    }
    return btoa(binary.join(""));
  }

  // Determine MIME type from file extension
  private _getMimeType(path: string): string {
    const extension = path.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "png":
        return "image/png";
      case "jpg":
      case "jpeg":
        return "image/jpeg";
      case "gif":
        return "image/gif";
      case "svg":
        return "image/svg+xml";
      case "ico":
        return "image/x-icon";
      default:
        return "application/octet-stream";
    }
  }

  // Request persistent storage permission
  async requestPersistentStorage(): Promise<boolean> {
    if (navigator.storage && navigator.storage.persist) {
      const isPersisted = await navigator.storage.persist();
      console.log(`Persistent storage granted: ${isPersisted}`);
      return isPersisted;
    }
    return false;
  }

  // Start auto-save timer
  private startAutoSave(intervalMs: number = 5000): void {
    this.stopAutoSave();

    this.autoSaveTimer = setInterval(() => {
      this.saveToIndexedDB().catch((err) => {
        console.error("Auto-save error:", err);
      });
    }, intervalMs);
  }

  // Stop auto-save timer
  private stopAutoSave(): void {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  }

  // Trigger an immediate auto-save
  triggerAutoSave(): void {
    this.saveToIndexedDB().catch((err) => {
      console.error("Triggered auto-save error:", err);
    });
  }

  public async listFilesInDirectory(path: string): Promise<string[]> {
    try {
      return await pyodideService.listFilesInDirectory(path);
    } catch (error) {
      console.error("Error listing directory:", error);
      return [];
    }
  }

  clearPendingFiles(): void {
    this.pendingFiles = {};
  }

  public isReady(): boolean {
    return this.isWorkerMode ? pyodideService.isReady() : this.pyodide !== null;
  }
}

export const filesystemService = new FilesystemService();
